// SAB-1093: resolve what ACTUALLY happened to a deposit before telling the user it failed.
//
// The first real production PayPal deposit was credited correctly and reported as
// "payment failed". paypal-webhook races paypal-capture-order and often wins; the capture
// call then returned a non-success response while the money was already in the wallet.
// The server race is fixed at its source (paypal-capture-order no longer filters the row
// lookup on status='pending'), but a capture response can still be lost to a network drop,
// a timeout, or a backgrounded tab — and in every one of those cases the webhook still
// credits. A single failed response is therefore NOT evidence that a deposit failed.
//
// That false failure is not merely cosmetic: it is what makes a user retry. SAB-1090's
// dedupe absorbs a retry inside 15 minutes; past that window a retry is a second charge.
// So the rule is: never show failure until the row says so, or until we have genuinely
// stopped being able to tell.
//
// `wallet_transactions.status` vocabulary is fixed by wallet_transactions_status_check:
//   'pending' | 'completed' | 'failed' | 'refunded'
// A deposit that never completes stays 'pending' forever (there is no writer that sets
// 'failed' on an abandoned PayPal checkout), so exhaustion — not a status — is how an
// abandoned checkout ends up here. That is why UNRESOLVED is a distinct outcome from
// FAILED, and why the copy for it must not claim money did or did not move.

import { supabase } from '@/integrations/supabase/client';

export type DepositOutcome = 'completed' | 'failed' | 'unresolved';

/** Terminal-negative statuses. Anything else that is not 'completed' is still in flight. */
export const TERMINAL_FAILURE_STATUSES = ['failed', 'refunded'] as const;

/**
 * Classify a single observation of the row. Pure, so the decision is testable without a
 * clock or a network: the bug was a wrong DECISION, not a wrong query.
 */
export function classifyDepositStatus(status: string | null | undefined): DepositOutcome {
  if (status === 'completed') return 'completed';
  if (status && (TERMINAL_FAILURE_STATUSES as readonly string[]).includes(status)) return 'failed';
  // 'pending', null (row not visible yet), or anything unrecognised → keep waiting.
  // Treating an unknown status as failure is precisely the mistake being fixed.
  return 'unresolved';
}

export interface PollOptions {
  /** Total attempts before giving up. 20 x 1500ms ≈ 30s, comfortably past observed webhook latency (31s worst case seen: 22:42:23 → 22:42:54). */
  attempts?: number;
  intervalMs?: number;
  /** Injected for tests. Returns the row's current status, or null if not visible. */
  fetchStatus?: (orderId: string) => Promise<string | null>;
  /** Injected for tests so the suite does not sleep. */
  sleep?: (ms: number) => Promise<void>;
}

async function defaultFetchStatus(orderId: string): Promise<string | null> {
  // `wallet_transactions` is absent from the generated Database types, so the typed
  // .from() overload does not resolve for it (the same reason the existing
  // NFLFundsSection queries are in the type-error baseline). Cast at the client rather
  // than add to that baseline; the shape actually read is asserted just below.
  const { data, error } = await (supabase as unknown as {
    from: (t: string) => any;
  })
    .from('wallet_transactions')
    .select('status')
    .filter('metadata->>paypal_order_id', 'eq', orderId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  // A read error is NOT a failed deposit — it is a failure to observe. Stay unresolved and
  // let the caller keep trying; reporting failure here would recreate the original bug.
  if (error) return null;
  return (data as { status?: string } | null)?.status ?? null;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Poll until the deposit resolves. Returns as soon as the row reaches a terminal state;
 * returns 'unresolved' if it never does within the budget.
 *
 * 'unresolved' must NOT be rendered as failure. The webhook remains the source of truth and
 * will still credit afterwards — the honest message covers both outcomes without claiming
 * money did or did not move.
 */
export async function pollDepositResolution(
  orderId: string,
  opts: PollOptions = {},
): Promise<DepositOutcome> {
  const {
    attempts = 20,
    intervalMs = 1500,
    fetchStatus = defaultFetchStatus,
    sleep = defaultSleep,
  } = opts;

  for (let i = 0; i < attempts; i++) {
    const outcome = classifyDepositStatus(await fetchStatus(orderId));
    if (outcome !== 'unresolved') return outcome;
    // Don't sleep after the final look — it delays the answer without another observation.
    if (i < attempts - 1) await sleep(intervalMs);
  }
  return 'unresolved';
}
